import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Velvet.css';

/* ── Filter map ─────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;

/* ── Ornamental SVGs ────────────────────────────────────────── */
const OrnamentCorner = ({ className }) => (
  <svg className={`vel-ornament-corner ${className || ''}`} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 76 L4 4 L76 4" stroke="currentColor" strokeWidth="1" fill="none"/>
    <path d="M4 76 L4 20 L20 4" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.5"/>
    <circle cx="4" cy="4" r="2.5" fill="currentColor"/>
    <circle cx="76" cy="4" r="1.5" fill="currentColor"/>
    <circle cx="4" cy="76" r="1.5" fill="currentColor"/>
    <path d="M4 40 L10 40" stroke="currentColor" strokeWidth="1"/>
    <path d="M40 4 L40 10" stroke="currentColor" strokeWidth="1"/>
  </svg>
);

const GoldDivider = () => (
  <svg className="vel-gold-divider" viewBox="0 0 400 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="0" y1="12" x2="160" y2="12" stroke="currentColor" strokeWidth="0.8" opacity="0.6"/>
    <path d="M175 12 L183 4 L191 12 L183 20 Z" fill="currentColor" opacity="0.8"/>
    <path d="M195 12 L200 7 L205 12 L200 17 Z" fill="currentColor"/>
    <path d="M209 12 L217 4 L225 12 L217 20 Z" fill="currentColor" opacity="0.8"/>
    <line x1="240" y1="12" x2="400" y2="12" stroke="currentColor" strokeWidth="0.8" opacity="0.6"/>
  </svg>
);

const GoldRule = ({ className }) => (
  <div className={`vel-gold-rule ${className || ''}`}>
    <span className="vel-gold-rule-line" />
    <span className="vel-gold-rule-diamond" />
    <span className="vel-gold-rule-line" />
  </div>
);

/* ── Countdown ──────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({
        d: Math.floor(ms / 86400000),
        h: Math.floor(ms % 86400000 / 3600000),
        m: Math.floor(ms % 3600000 / 60000),
        s: Math.floor(ms % 60000 / 1000)
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);

  return (
    <div className="vel-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="vel-cu">
          <span className="vel-cu-val">{String(v).padStart(2, '0')}</span>
          <span className="vel-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Calendar ───────────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++)    cells.push({ n: i, cur: true });
  while (cells.length < 42)         cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return (
    <div className="vel-calendar">
      <div className="vel-cal-header">
        <span className="vel-cal-month">{MON[mo]}</span>
        <span className="vel-cal-year">{yr}</span>
      </div>
      <div className="vel-cal-grid">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(h => (
          <div key={h} className="vel-cal-head">{h}</div>
        ))}
        {cells.map((c, i) => {
          const isWedding = c.cur && c.n === day;
          return (
            <div key={i}
              className={`vel-cal-cell${!c.cur ? ' vel-cal-muted' : ''}${isWedding ? ' vel-cal-wedding' : ''}`}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVP Form ──────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status, setStatus] = useState('confirmed');
  const [name, setName] = useState('');
  const [survey, setSurvey] = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="vel-rsvp-done">
      <GoldDivider />
      <strong className="vel-shimmer-text">Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="vel-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="vel-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' }, { v: 'declined', l: bc.rsvp?.noText || 'Не смогу прийти' }].map(o => (
          <label key={o.v}
            className={`vel-rsvp-opt${status === o.v ? ' active' : ''}`}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="vel-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="vel-survey-q">
          <p className="vel-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="vel-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="vel-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`vel-rsvp-opt vel-rsvp-opt-sm${selected ? ' active' : ''}`}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={selected}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="vel-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`vel-rsvp-opt vel-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p className="vel-field-label">{bc.message.fieldLabel}</p>
          )}
          <textarea className="vel-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}
      <button type="submit" className="vel-btn">
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ───────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="vel-wishlist">
      {items.map(item => (
        <div key={item.id} className="vel-wish-item">
          <div className="vel-wish-main">
            <div className="vel-wish-name">{item.name}</div>
            {item.price && <div className="vel-wish-price">{item.price} ₽</div>}
          </div>
          <div className="vel-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="vel-btn-outline">
                Посмотреть
              </a>
            )}
            {reserved[item.id]
              ? <button type="button" className="vel-btn vel-btn--muted"
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  Снять резерв
                </button>
              : <button type="button" className="vel-btn"
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Scroll-reveal hook ─────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.vel-rise, .vel-fade, .vel-expand';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('vel-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('vel-in');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ══════════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════════ */
export default function Velvet({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc     = project.block_content || {};
  const accent = project.palette?.primary || '#d4af37';

  const fontTitle = project.fonts?.title || null;
  const fontBody  = project.fonts?.body  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-title': `'${fontTitle}', serif` } : {}),
    ...(fontBody  ? { '--f-body':  `'${fontBody}', sans-serif` } : {}),
    '--c-accent': accent,
  };

  useEffect(() => {
    const families = [fontTitle, fontBody].filter(Boolean);
    if (!families.length) return;
    const id = 'vel-dynamic-fonts';
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const query = families
      .map(f => `family=${encodeURIComponent(f)}:wght@300;400;600;700;800`)
      .join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  const photos = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const p1 = photos[0];
  const invPhotos = project.invitation_photos || [];
  const dcPhotos = project.dresscode_photos || [];

  const bride = project.bride || 'Невеста';
  const groom = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const fmtDate = raw => {
    if (!raw) return 'ДД · ММ · ГГГГ';
    const d = new Date(raw);
    const months = ['ЯНВАРЯ','ФЕВРАЛЯ','МАРТА','АПРЕЛЯ','МАЯ','ИЮНЯ','ИЮЛЯ','АВГУСТА','СЕНТЯБРЯ','ОКТЯБРЯ','НОЯБРЯ','ДЕКАБРЯ'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');
  const HAS_CALENDAR  = blocks.includes('calendar');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  return (
    <div className="vel-page" ref={pageRef} style={fontStyle}>

      {/* ══ HERO (split layout) ══════════════════════════════════ */}
      <section className="vel-hero">
        {/* Left panel — dark, text */}
        <div className="vel-hero-left vel-hero-slide-left">
          {/* Ornament corners */}
          <OrnamentCorner className="vel-oc--tl" />
          <OrnamentCorner className="vel-oc--tr" />
          <OrnamentCorner className="vel-oc--bl" />
          <OrnamentCorner className="vel-oc--br" />

          <div className="vel-hero-content">
            <div className="vel-hero-eyebrow">Приглашение на свадьбу</div>
            <GoldRule className="vel-hero-rule" />
            <h1 className="vel-hero-names">
              <span className="vel-hero-bride vel-shimmer-text">{bride}</span>
              <span className="vel-hero-connector">&amp;</span>
              <span className="vel-hero-groom vel-shimmer-text">{groom}</span>
            </h1>
            <GoldRule />
            <div className="vel-hero-date">{fmtDate(weddingDate)}</div>
            {project.wedding_time && (
              <div className="vel-hero-time">{project.wedding_time}</div>
            )}
            <p className="vel-hero-slogan">
              {bc.hero?.subtitle || project.wedding_slogan || 'Мы рады видеть вас рядом в этот особенный день'}
            </p>
            {HAS_RSVP && (
              <a href="#rsvp" className="vel-btn vel-hero-btn">
                Заполнить анкету
              </a>
            )}
          </div>
        </div>

        {/* Right panel — photo */}
        <div className="vel-hero-right vel-hero-slide-right">
          {p1
            ? <img className="vel-hero-photo" src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
            : <div className="vel-hero-photo-placeholder">
                <div className="vel-hero-photo-placeholder-inner">
                  <GoldDivider />
                  <span>{bride} &amp; {groom}</span>
                </div>
              </div>
          }
          <div className="vel-hero-photo-overlay" />
        </div>
      </section>

      {/* ══ INVITATION ══════════════════════════════════════════ */}
      <section className="vel-section" id="invitation">
        <div className="vel-section-inner">
          <GoldDivider />
          <div className="vel-two-col">
            <div className="vel-rise">
              <h2 className="vel-section-title"
                dangerouslySetInnerHTML={{ __html: bc.message?.title || 'Дорогие гости' }} />
              <div className="vel-body-text">
                {bc.message?.text
                  ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
                  : (
                    <>
                      <p>С трепетом и восторгом мы хотим <strong>пригласить вас</strong> стать частью нашего торжества — дня, который навсегда останется в наших сердцах.</p>
                      <p>Ваше присутствие — лучший подарок, который мы могли бы пожелать.</p>
                    </>
                  )
                }
              </div>
              {HAS_RSVP && (
                <a href="#rsvp" className="vel-btn" style={{ marginTop: '24px', display: 'inline-flex' }}>
                  {bc.rsvp?.btnText || 'Заполнить анкету'}
                </a>
              )}
            </div>

            <div className="vel-invitation-right vel-fade" style={{ '--d': '.15s' }}>
              {/* Photos */}
              {invPhotos.length > 0 ? (
                <div className="vel-inv-photos">
                  {invPhotos.slice(0, 2).map((ph, i) => (
                    <div key={i} className={`vel-inv-photo vel-inv-photo--${i}`}>
                      <img src={src(ph.url)} alt="" style={{ filter: flt(ph), objectPosition: objPos(ph) }} />
                    </div>
                  ))}
                </div>
              ) : p1 ? (
                <div className="vel-inv-photos">
                  <div className="vel-inv-photo vel-inv-photo--0">
                    <img src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
                  </div>
                </div>
              ) : (
                <div className="vel-inv-placeholder">
                  <GoldDivider />
                  <span>Ваши фотографии</span>
                </div>
              )}

              {/* Calendar */}
              <CalendarWidget
                date={weddingDate}
                accent={accent}
                addBtn={HAS_CALENDAR && (() => {
                  const d = new Date(weddingDate);
                  const pad = n => String(n).padStart(2, '0');
                  const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
                  const title = encodeURIComponent(`Свадьба ${project.bride || ''} & ${project.groom || ''}`);
                  const href = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="vel-cal-add-btn">
                      {bc.calendar?.title || 'Добавить в календарь'}
                    </a>
                  );
                })()}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══ COUNTDOWN ═══════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <section className="vel-section vel-section--surface" id="countdown">
          <div className="vel-section-inner">
            <GoldDivider />
            <div className="vel-two-col">
              <div className="vel-rise">
                <h2 className="vel-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.countdown?.title || 'До торжества' }} />
                <BlockSubtitle text={bc.countdown?.subtitle} className="vel-body-text vel-muted" />
              </div>
              <div className="vel-fade" style={{ '--d': '.15s' }}>
                <Countdown date={weddingDate} accent={accent} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══ SCHEDULE ════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <section className="vel-section" id="schedule">
          <div className="vel-section-inner">
            <GoldDivider />
            <h2 className="vel-section-title vel-rise"
              dangerouslySetInnerHTML={{ __html: bc.schedule?.title || 'Программа вечера' }} />
            <BlockSubtitle text={bc.schedule?.subtitle} className="vel-body-text vel-muted vel-rise" style={{ marginBottom: '32px', '--d': '.1s' }} />
            <div className="vel-schedule">
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <>
                      <div className="vel-sched-group-title vel-rise">Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div className="vel-sched-item vel-rise" key={`opt-${i}`} style={{ '--d': `${i * 0.08}s` }}>
                          <div className="vel-sched-time">{item.time}</div>
                          <div className="vel-sched-sep">
                            <span className="vel-sched-diamond" />
                          </div>
                          <div className="vel-sched-info">
                            <div className="vel-sched-event">{item.event}</div>
                            {item.description && <div className="vel-sched-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                      <div className="vel-sched-group-title vel-rise" style={{ '--d': '.05s' }}>Основная программа</div>
                    </>
                  )}
                  {mainItems.map((item, i) => (
                    <div className="vel-sched-item vel-rise" key={`main-${i}`} style={{ '--d': `${i * 0.08}s` }}>
                      <div className="vel-sched-time">{item.time}</div>
                      <div className="vel-sched-sep">
                        <span className="vel-sched-diamond" />
                      </div>
                      <div className="vel-sched-info">
                        <div className="vel-sched-event">{item.event}</div>
                        {item.description && <div className="vel-sched-desc">{item.description}</div>}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                [['14:00', 'Сбор гостей'], ['15:00', 'Церемония'], ['16:30', 'Фотосессия'], ['18:00', 'Банкет']].map(([t, e], i) => (
                  <div className="vel-sched-item vel-rise" key={i} style={{ opacity: 0.35 }}>
                    <div className="vel-sched-time">{t}</div>
                    <div className="vel-sched-sep"><span className="vel-sched-diamond" /></div>
                    <div className="vel-sched-info"><div className="vel-sched-event">{e}</div></div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ MAP ═════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <section className="vel-section vel-section--surface" id="map">
          <div className="vel-section-inner">
            <GoldDivider />
            <h2 className="vel-section-title vel-rise"
              dangerouslySetInnerHTML={{ __html: bc.map?.title || 'Место проведения' }} />
            <BlockSubtitle text={bc.map?.subtitle} className="vel-body-text vel-muted vel-rise" style={{ marginBottom: '24px', '--d': '.1s' }} />
            <div className="vel-venues">
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="vel-venue-row vel-rise" style={{ '--d': `${i * 0.1}s` }}>
                  <div className="vel-venue-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="vel-venue-info">
                    {v.label && <span className="vel-venue-label">{v.label}</span>}
                    {v.name && <span className="vel-venue-name">{v.name} — </span>}
                    <span className="vel-venue-addr">{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                    target="_blank" rel="noopener noreferrer" className="vel-btn-outline">
                    Маршрут
                  </a>
                </div>
              )) : project.wedding_address ? (
                <div className="vel-venue-row">
                  <div className="vel-venue-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <span className="vel-venue-addr">{project.wedding_address}</span>
                </div>
              ) : (
                <div className="vel-venue-row" style={{ opacity: 0.5, fontSize: '0.9rem' }}>
                  Адрес будет добавлен позднее
                </div>
              )}
            </div>
            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="vel-map-frame vel-rise" style={{ '--d': '.2s' }}>
              {(() => {
                if (project.map_url) {
                  return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const pts  = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  const zoom = withCoords.length > 1 ? 12 : 16;
                  const mapSrc = `https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=${zoom}&pt=${pts}&lang=ru_RU`;
                  return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const addr = venues.length > 0
                  ? venues.map(v => v.address).filter(Boolean).join(' — ')
                  : (project.wedding_address || '');
                if (!addr) {
                  return <MapFrame region={project.region} title="Карта" src="https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const mapSrc = venues.length > 1
                  ? `https://yandex.ru/map-widget/v1/?rtext=${venues.map(v => encodeURIComponent(v.address)).join('~')}&rtt=auto&lang=ru_RU`
                  : `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
              })()}
            </div>
            )}
          </div>
        </section>
      )}

      {/* ══ DRESSCODE ═══════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <section className="vel-section" id="dresscode">
          <div className="vel-section-inner">
            <GoldDivider />
            <div className="vel-two-col">
              <div className="vel-rise">
                <h2 className="vel-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.palette?.title || 'Дресс-код' }} />
                <BlockSubtitle text={bc.palette?.subtitle} className="vel-body-text" tag="div" />
                <div className="vel-swatches">
                  {(project.dresscode_palette || []).map((sw, i) => {
                    const c = sw.color || '#ffffff';
                    const hex = c.replace('#', '');
                    const r = parseInt(hex.slice(0, 2), 16) || 255;
                    const g = parseInt(hex.slice(2, 4), 16) || 255;
                    const b = parseInt(hex.slice(4, 6), 16) || 255;
                    const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 160;
                    return (
                      <div key={i} className="vel-swatch vel-rise"
                        style={{ background: c, '--d': `${i * 0.08}s` }}>
                        <div className="vel-swatch-border" />
                        {sw.label && <span style={{ color: isLight ? '#1a0810' : '#faf0e6' }}>{sw.label}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {dcPhotos.length > 0 && (
                <div className="vel-dc-photos vel-fade" style={{ '--d': '.2s' }}>
                  {dcPhotos.slice(0, 2).map((p, i) => (
                    <div key={i} className="vel-dc-photo">
                      <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ RSVP + WISHLIST ═════════════════════════════════════ */}
      {(HAS_RSVP || HAS_WISHLIST) && (
        <section className="vel-section vel-section--surface" id="rsvp">
          <div className="vel-section-inner">
            <GoldDivider />
            {HAS_RSVP && (
              <div className="vel-card vel-rise">
                <h2 className="vel-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.rsvp?.title || 'Анкета гостя' }} />
                <BlockSubtitle text={bc.rsvp?.subtitle} className="vel-body-text" style={{ marginBottom: '24px' }} />
                {bc.rsvp?.deadline && (
                  <p className="vel-body-text" style={{ marginBottom: '20px', color: accent }}>
                    Ответьте до: <strong>{new Date(bc.rsvp.deadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                  </p>
                )}
                <RSVPForm project={project} accent={accent} />
              </div>
            )}
            {HAS_WISHLIST && (
              <div className="vel-card vel-rise" style={{ '--d': '.15s' }}>
                <h2 className="vel-section-title"
                  dangerouslySetInnerHTML={{ __html: bc.wishlist?.title || 'Список желаний' }} />
                <BlockSubtitle text={bc.wishlist?.subtitle} className="vel-body-text" style={{ marginBottom: '24px' }} />
                <Wishlist project={project} accent={accent} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ WATERMARK ═══════════════════════════════════════════ */}
      {(!project.payment_plan || project.payment_plan === 'free') && (
        <div style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
          background: 'rgba(26,8,16,0.92)', color: '#faf0e6',
          borderRadius: '8px', padding: '8px 14px',
          fontSize: '0.75rem', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(212,175,55,0.3)',
        }}>
          Создано на <strong style={{ color: '#d4af37' }}>Amorika</strong>
        </div>
      )}

      {/* ══ FOOTER ══════════════════════════════════════════════ */}
      <footer className="vel-footer">
        <div className="vel-footer-ornaments">
          <OrnamentCorner className="vel-oc--tl" />
          <OrnamentCorner className="vel-oc--tr" />
          <OrnamentCorner className="vel-oc--bl" />
          <OrnamentCorner className="vel-oc--br" />
        </div>
        <div className="vel-footer-inner">
          <GoldDivider />
          <div className="vel-footer-names">
            <span className="vel-shimmer-text">{bride}</span>
            <span className="vel-footer-amp">&amp;</span>
            <span className="vel-shimmer-text">{groom}</span>
          </div>
          <div className="vel-footer-date">{fmtDate(weddingDate)}</div>
          <GoldDivider />
          <div className="vel-footer-slogan">
            {project.wedding_slogan || 'Одна любовь на всю жизнь'}
          </div>
          <div className="vel-footer-brand">
            Создано на <a href="/" style={{ color: accent, textDecoration: 'none' }}>Amorika</a>
          </div>
        </div>
      </footer>

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
